'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface FooterLink {
  label: string;
  href: string;
}

interface FooterColumn {
  title: string;
  links: FooterLink[];
}

interface SocialLink {
  name: string;
  icon: string;
  href: string;
}

interface FooterSectionProps {
  columns: FooterColumn[];
  socialLinks: SocialLink[];
  companyName: string;
}

export default function FooterSection({ columns, socialLinks, companyName }: FooterSectionProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentYear, setCurrentYear] = useState('2025');

  useEffect(() => {
    setIsHydrated(true);
    setCurrentYear(new Date().getFullYear().toString());
  }, []);

  if (!isHydrated) {
    return (
      <footer className="bg-foreground text-primary-foreground py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {columns.map((column, index) => (
              <div key={index}>
                <h3 className="text-lg font-headline font-bold mb-4">{column.title}</h3>
                <ul className="space-y-3">
                  {column.links.map((link, linkIndex) => (
                    <li key={linkIndex}>
                      <Link
                        href={link.href}
                        className="text-primary-foreground/70 hover:text-primary-foreground font-body transition-colors duration-300"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          
          <div className="border-t border-primary-foreground/20 pt-8 flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <p className="text-primary-foreground/70 font-body text-sm">
              &copy; 2025 {companyName}. All rights reserved.
            </p>
            <div className="flex items-center space-x-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-primary-foreground rounded-full flex items-center justify-center hover:bg-primary-foreground/20"
                  aria-label={social.name}
                >
                  <img src={social.icon} alt={social.name} loading="lazy" className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-foreground text-primary-foreground py-12 lg:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {columns.map((column, index) => (
            <div key={index}>
              <h3 className="text-lg font-headline font-bold mb-4">{column.title}</h3>
              <ul className="space-y-3">
                {column.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <Link
                      href={link.href}
                      className="text-primary-foreground/70 hover:text-primary-foreground font-body transition-colors duration-300"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        
        <div className="border-t border-primary-foreground/20 pt-8 flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
          <p className="text-primary-foreground/70 font-body text-sm">
            &copy; {currentYear} {companyName}. All rights reserved.
          </p>
          <div className="flex items-center space-x-4">
            {socialLinks.map((social, index) => (
              <a
                key={index}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-primary-foreground rounded-full flex items-center justify-center hover:bg-primary-foreground/20 transition-colors duration-300"
                aria-label={social.name}
              >
                <img src={social.icon} alt={social.name} loading="lazy" className="w-5 h-5" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}